import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { FileText, ArrowLeft, Sparkles, RefreshCw, Plus, Trash2 } from "lucide-react";
import { formatMoney } from "@/lib/money";
import { useClientsPickerQuery } from "@/hooks/usePortalQueries";

const inputCls = "mt-1 w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#FFC817]/50";
const labelCls = "text-[10px] uppercase tracking-wider text-[#A8A29E]";

const CheckField = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center gap-2 text-xs text-[#57534E] cursor-pointer">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} className="h-4 w-4 accent-[#FFC817]" />
    {label}
  </label>
);

const DEFAULTS = {
  project_type: "Corporate", production_type: "Full", country: "India", currency: "INR",
  billing_treated_as_domestic: false, client_is_foreign_entity: false,
  client_type: "SME", creative_direction_by: "NJM",
  pre_production: false, production: true, post_production: false,
  drone: false, voiceover: false, voiceover_languages: [], animation: false,
  raw_footage_deliverable: false, ip_ownership: "NJM-retains-portfolio",
  on_site_third_party_premises: false, client_brings_own_crew: false,
  health_checkup_required: false, confidentiality_required: false, shoot_involves_people: false,
  payment_schedule: [
    { label: "Advance", percent: 50, trigger: "due immediately before work commences" },
    { label: "Balance", percent: 50, trigger: "due on final delivery" },
  ],
};

export function BriefForm({ existingBrief, initial, onGenerated, onCancel }) {
  const { data: clients = [] } = useClientsPickerQuery();
  const [clientId, setClientId] = useState(existingBrief?.client_id || initial?.client_id || "");
  const [clientQuotes, setClientQuotes] = useState([]);
  const [form, setForm] = useState({ ...DEFAULTS, ...(existingBrief || {}), ...(initial || {}) });
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (initial?.project_id && !form.project_name) {
      api.get(`/v1/projects/${initial.project_id}`).then((r) => {
        setForm((f) => ({ ...f, project_name: f.project_name || r.data.project?.name || "" }));
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial?.project_id]);

  useEffect(() => {
    if (!clientId) { setClientQuotes([]); return; }
    api.get(`/v1/clients/${clientId}`).then((r) => {
      setClientQuotes(r.data.quotes || []);
      const client = (r.data.client || {});
      setForm((f) => ({ ...f, client_id: clientId, client_name: client.name || f.client_name }));
    }).catch(() => {});
  }, [clientId]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const schedule = form.payment_schedule || [];
  const scheduleTotal = schedule.reduce((sum, r) => sum + (Number(r.percent) || 0), 0);
  const updateMilestone = (idx, field, value) => {
    const next = schedule.map((r, i) => (i === idx ? { ...r, [field]: value } : r));
    set("payment_schedule", next);
  };
  const addMilestone = () => set("payment_schedule", [...schedule, { label: "", percent: 0, trigger: "" }]);
  const removeMilestone = (idx) => set("payment_schedule", schedule.filter((_, i) => i !== idx));

  const generate = async () => {
    if (!form.project_name) { toast.error("Give this document a project name"); return; }
    setGenerating(true);
    try {
      const briefRes = await api.post("/v1/commercial-docs/briefs", form);
      const docRes = await api.post("/v1/commercial-docs/documents/generate", { brief_id: briefRes.data.id });
      toast.success("SOW generated from the Rule Engine");
      onGenerated(docRes.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      console.error("commercial-docs generate failed:", e?.response?.status, detail || e?.message);
      toast.error(detail?.code || "Couldn't generate document. See console for details");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="brief-form-page">
      <div className="flex items-center gap-3">
        <button onClick={onCancel} data-testid="brief-form-back-btn" className="inline-flex items-center gap-1.5 rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-1.5 text-xs text-[#57534E] hover:border-[#FFC817] hover:text-[#B45309]">
          <ArrowLeft className="h-3.5 w-3.5" /> All Documents
        </button>
      </div>
      <div>
        <h1 className="font-editorial text-3xl tracking-tight flex items-center gap-2"><FileText className="h-6 w-6 text-[#B45309]" /> New Project Brief</h1>
        <p className="text-sm text-[#57534E] mt-1">A deterministic Rule Engine selects the exact clauses for this SOW from your answers below — every clause is fully explainable, never guessed by AI.</p>
      </div>

      <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-5 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Client</label>
            <select data-testid="brief-client-select" value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputCls}>
              <option value="">Select client…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Attach a quote (optional)</label>
            <select data-testid="brief-quote-select" value={form.quote_id || ""} onChange={(e) => set("quote_id", e.target.value)} className={inputCls} disabled={!clientId}>
              <option value="">No quote — generate without costing</option>
              {clientQuotes.map((q) => <option key={q.id} value={q.id}>{q.name} — {formatMoney(q.quoted_total, q.currency)} ({q.outcome || "Open"})</option>)}
            </select>
            {clientId && clientQuotes.length === 0 && (
              <div className="text-[10px] text-[#78716C] mt-1" data-testid="brief-no-quotes-note">This client has no quotes yet — the SOW will generate without a costing section.</div>
            )}
          </div>
          <div>
            <label className={labelCls}>Project Name</label>
            <input data-testid="brief-project-name-input" value={form.project_name || ""} onChange={(e) => set("project_name", e.target.value)} className={inputCls} placeholder="e.g. Q3 Brand Film" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Project Type</label>
            <select data-testid="brief-project-type-select" value={form.project_type} onChange={(e) => set("project_type", e.target.value)} className={inputCls}>
              {["Corporate", "Wedding", "Photography", "Event", "Product", "Documentary", "Testimonial", "Training", "Reel"].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Production Type</label>
            <select data-testid="brief-production-type-select" value={form.production_type} onChange={(e) => set("production_type", e.target.value)} className={inputCls}>
              {["Full", "Support-Only", "Client-Directed"].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Client Type</label>
            <select data-testid="brief-client-type-select" value={form.client_type} onChange={(e) => set("client_type", e.target.value)} className={inputCls}>
              {["Enterprise", "SME", "Agency", "Institution", "Individual"].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Creative Direction By</label>
            <select data-testid="brief-creative-direction-select" value={form.creative_direction_by} onChange={(e) => set("creative_direction_by", e.target.value)} className={inputCls}>
              {["NJM", "Client"].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Country</label>
            <input data-testid="brief-country-input" value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Currency</label>
            <select data-testid="brief-currency-select" value={form.currency} onChange={(e) => set("currency", e.target.value)} className={inputCls}>
              {["INR", "USD", "AED", "GBP", "EUR"].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>IP Ownership</label>
            <select data-testid="brief-ip-ownership-select" value={form.ip_ownership} onChange={(e) => set("ip_ownership", e.target.value)} className={inputCls}>
              <option value="NJM-retains-portfolio">NJM retains + portfolio rights</option>
              <option value="Client-owns">Client owns (full licence + IP indemnity)</option>
            </select>
          </div>
          <div className="flex flex-col justify-end gap-1.5 pb-1">
            <CheckField label="Foreign entity, billed in INR (offshore exception)" checked={form.client_is_foreign_entity} onChange={(v) => { set("client_is_foreign_entity", v); if (v) set("billing_treated_as_domestic", true); }} testId="brief-foreign-entity-billed-inr-check" />
          </div>
        </div>

        <div>
          <div className={`${labelCls} mb-2`}>Scope</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <CheckField label="Pre-production" checked={form.pre_production} onChange={(v) => set("pre_production", v)} testId="brief-pre-production-check" />
            <CheckField label="Production (shoot)" checked={form.production} onChange={(v) => set("production", v)} testId="brief-production-check" />
            <CheckField label="Post-production" checked={form.post_production} onChange={(v) => set("post_production", v)} testId="brief-post-production-check" />
            <CheckField label="Drone" checked={form.drone} onChange={(v) => set("drone", v)} testId="brief-drone-check" />
            <CheckField label="Voiceover" checked={form.voiceover} onChange={(v) => set("voiceover", v)} testId="brief-voiceover-check" />
            <CheckField label="Animation" checked={form.animation} onChange={(v) => set("animation", v)} testId="brief-animation-check" />
            <CheckField label="Raw footage deliverable" checked={form.raw_footage_deliverable} onChange={(v) => set("raw_footage_deliverable", v)} testId="brief-raw-footage-check" />
          </div>
        </div>

        <div>
          <div className={`${labelCls} mb-2`}>Risk & Legal</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <CheckField label="On-site at third-party premises" checked={form.on_site_third_party_premises} onChange={(v) => set("on_site_third_party_premises", v)} testId="brief-onsite-third-party-check" />
            <CheckField label="Client brings own crew" checked={form.client_brings_own_crew} onChange={(v) => set("client_brings_own_crew", v)} testId="brief-client-own-crew-check" />
            <CheckField label="Health checkup required" checked={form.health_checkup_required} onChange={(v) => set("health_checkup_required", v)} testId="brief-health-checkup-check" />
            <CheckField label="Confidentiality required" checked={form.confidentiality_required} onChange={(v) => set("confidentiality_required", v)} testId="brief-confidentiality-check" />
            <CheckField label="Shoot involves identifiable people" checked={form.shoot_involves_people} onChange={(v) => set("shoot_involves_people", v)} testId="brief-shoot-involves-people-check" />
          </div>
        </div>

        <div data-testid="brief-payment-schedule-section">
          <div className={`${labelCls} mb-2`}>Payment Milestones</div>
          <p className="text-xs text-[#57534E] mb-2">Drives the "Payment Schedule" clause on the generated SOW — amounts are computed from the attached Quote's total. Not fixed to 50/50; add/remove tranches to match the real deal (e.g. 50/25/25 tied to deliverable approvals).</p>
          <div className="space-y-2">
            {schedule.map((row, idx) => (
              <div key={idx} className="flex items-center gap-2" data-testid={`brief-milestone-row-${idx}`}>
                <input value={row.label} onChange={(e) => updateMilestone(idx, "label", e.target.value)} placeholder="Label (e.g. Advance)" data-testid={`brief-milestone-label-${idx}`} className={`${inputCls} mt-0 flex-1`} />
                <input type="number" min="0" max="100" value={row.percent} onChange={(e) => updateMilestone(idx, "percent", Number(e.target.value))} placeholder="%" data-testid={`brief-milestone-percent-${idx}`} className={`${inputCls} mt-0 w-20`} />
                <input value={row.trigger} onChange={(e) => updateMilestone(idx, "trigger", e.target.value)} placeholder="Trigger (e.g. due on final delivery)" data-testid={`brief-milestone-trigger-${idx}`} className={`${inputCls} mt-0 flex-[2]`} />
                <button onClick={() => removeMilestone(idx)} data-testid={`brief-milestone-remove-${idx}`} className="rounded-md border border-[#E7E5E4] p-2 text-[#A8A29E] hover:text-red-600 hover:border-red-300">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-2">
            <button onClick={addMilestone} data-testid="brief-milestone-add-btn" className="inline-flex items-center gap-1 text-xs text-[#B45309] hover:underline">
              <Plus className="h-3.5 w-3.5" /> Add milestone
            </button>
            <span data-testid="brief-milestone-total" className={`text-xs ${Math.abs(scheduleTotal - 100) > 5 ? "text-red-600" : "text-[#57534E]"}`}>
              Total: {scheduleTotal}%{Math.abs(scheduleTotal - 100) > 5 ? " — should sum to ~100%" : ""}
            </span>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={generate} disabled={generating} data-testid="brief-generate-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-[#FFC817] px-5 py-2.5 text-sm font-medium text-[#1C1C1E] hover:bg-[#E6B415] disabled:opacity-50">
          {generating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {generating ? "Running Rule Engine…" : "Generate SOW"}
        </button>
      </div>
    </div>
  );
}
